import catalog from '../../../public/map-fragments/catalog.json';
import { tileHash, type WorldTerrainContext } from './procedural';

type Point = { x: number; y: number };
type Rect = { minX: number; minY: number; maxX: number; maxY: number };
export type RuntimeSceneObject = { id: string; objectType: string; variant: number; worldX: number; worldY: number };
export type RuntimeScene = { id: string; sceneKey: string; biome: string; objects: RuntimeSceneObject[]; clearings: Rect[] };
type Fragment = { towns: Array<{ objects: Array<Omit<RuntimeSceneObject, 'id'> & { id: number }> }>; clearings?: Rect[] };
type Transform = { x: number; y: number; cos: number; sin: number; source: Rect[] };
type Segment = { a: Point; b: Point; main: boolean };
const fragments = import.meta.glob<Fragment>(['/public/map-fragments/*.json', '!/public/map-fragments/runtime-authority-qa.json'], { eager: true, import: 'default' });
const transforms = new WeakMap<RuntimeScene, Transform>();
const CELL = 64;
const GAP = 2;
const ROAD_CLEARANCE = 4.5;
// Authored direction, inspected once from the preview compositions. Runtime never reads their roadNetwork.
const roadTangents: Record<string, [number, number]> = Object.fromEntries([
  'road-wreck', 'forest-ambush', 'road-shrine', 'watchhouse', 'meadow', 'rockfall',
  'caravan-wreck', 'road-well', 'dust-gully', 'border-post', 'forest', 'road-sign',
  'trail-markers', 'jungle-road',
].map(id => [id, [1, 1]]));
roadTangents['mountain-cart'] = [1, 0];
// These compositions need water, an actual ledge or a broken road rather than ordinary wilderness.
const unsupported = new Set(['herbalist-pool', 'nest-cliff', 'severed-trail']);
const active = catalog.scenes.filter(scene => scene.availability === 'active' && !unsupported.has(scene.id));

function corners(rect: Rect): Point[] {
  return [{ x: rect.minX, y: rect.minY }, { x: rect.maxX, y: rect.minY },
    { x: rect.maxX, y: rect.maxY }, { x: rect.minX, y: rect.maxY }];
}
function contains(rect: Rect, x: number, y: number): boolean {
  return x >= rect.minX && x <= rect.maxX && y >= rect.minY && y <= rect.maxY;
}
function bounds(points: Point[]): Rect {
  return { minX: Math.min(...points.map(p => p.x)), minY: Math.min(...points.map(p => p.y)),
    maxX: Math.max(...points.map(p => p.x)), maxY: Math.max(...points.map(p => p.y)) };
}
function overlaps(a: Rect, b: Rect): boolean {
  return a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY;
}
function forward(t: Transform, x: number, y: number): Point {
  return { x: t.x + x * t.cos - y * t.sin, y: t.y + x * t.sin + y * t.cos };
}
function inverse(t: Transform, x: number, y: number): Point {
  return { x: (x - t.x) * t.cos + (y - t.y) * t.sin, y: -(x - t.x) * t.sin + (y - t.y) * t.cos };
}
export function sceneClearsPoint(scene: RuntimeScene, x: number, y: number): boolean {
  const transform = transforms.get(scene);
  if (!transform) return scene.clearings.some(rect => contains(rect, x, y));
  const local = inverse(transform, x, y);
  return transform.source.some(rect => contains(rect, local.x, local.y));
}
function projection(point: Point, segment: Segment): Point {
  const dx = segment.b.x - segment.a.x, dy = segment.b.y - segment.a.y;
  const fraction = Math.max(0, Math.min(1, ((point.x - segment.a.x) * dx + (point.y - segment.a.y) * dy) / (dx * dx + dy * dy)));
  return { x: segment.a.x + fraction * dx, y: segment.a.y + fraction * dy };
}
function distance(point: Point, segment: Segment): number {
  const projected = projection(point, segment);
  return Math.hypot(point.x - projected.x, point.y - projected.y);
}
function distanceToRect(segment: Segment, rect: Rect): number {
  let low = 0, high = 1;
  for (const [start, delta, min, max] of [[segment.a.x, segment.b.x - segment.a.x, rect.minX, rect.maxX],
    [segment.a.y, segment.b.y - segment.a.y, rect.minY, rect.maxY]]) {
    if (Math.abs(delta!) < 1e-10) { if (start! < min! || start! > max!) high = -1; }
    else {
      const first = (min! - start!) / delta!, last = (max! - start!) / delta!;
      low = Math.max(low, Math.min(first, last)); high = Math.min(high, Math.max(first, last));
    }
  }
  if (low <= high) return 0;
  const pointDistance = (p: Point) => Math.hypot(Math.max(rect.minX - p.x, 0, p.x - rect.maxX), Math.max(rect.minY - p.y, 0, p.y - rect.maxY));
  return Math.min(pointDistance(segment.a), pointDistance(segment.b), ...corners(rect).map(p => distance(p, segment)));
}
function biomeAt(context: WorldTerrainContext, point: Point): string {
  let biome = context.wildernessBiome ?? 'meadow', nearest = Infinity;
  for (const town of context.towns) {
    const d = Math.hypot(point.x - town.cx, point.y - town.cy);
    if (d < nearest) { nearest = d; biome = town.biome; }
  }
  return biome;
}

/** Presentation-only placements. The bounded owner cache may be evicted without changing any result. */
export class RuntimeSceneIndex {
  private readonly cells = new Map<string, readonly RuntimeScene[]>();
  private readonly segments: Segment[];
  private readonly clearanceSegments: Segment[];

  constructor(private readonly context: WorldTerrainContext) {
    const paths = context.roadPaths ?? context.roadNetwork.map(points => ({ points, kind: 'main' as const }));
    // Stable server roads own placement; the temporary selected route cannot move scenery.
    const stable = paths.some(path => path.points.length > 1) ? paths
      : context.networkCenters.length > 1 ? [{ points: [...context.networkCenters, context.networkCenters[0]!], kind: 'main' as const }]
        : context.activeRoute ? [{ points: context.activeRoute, kind: 'main' as const }] : [];
    this.segments = stable.flatMap(path => path.points.slice(1).flatMap((b, i) => {
      let a = path.points[i]!;
      if (![a.x, a.y, b.x, b.y].every(Number.isFinite) || Math.hypot(b.x - a.x, b.y - a.y) < 1e-8) return [];
      if (a.x > b.x || (a.x === b.x && a.y > b.y)) [a, b] = [b, a];
      return [{ a, b, main: path.kind === 'main' }];
    })).sort((a, b) => a.a.x - b.a.x || a.a.y - b.a.y || a.b.x - b.b.x || a.b.y - b.b.y);
    // A selected route can suppress an unsafe scene, but cannot become its parent road
    // while a stable graph exists. This matches the terrain renderer's route corridor.
    const route = context.activeRoute ?? [];
    this.clearanceSegments = [...this.segments, ...route.slice(1).flatMap((b, i) => {
      const a = route[i]!;
      return [a.x, a.y, b.x, b.y].every(Number.isFinite) && Math.hypot(b.x - a.x, b.y - a.y) > 1e-8
        ? [{ a, b, main: true }] : [];
    })];
  }

  clear(): void { this.cells.clear(); }

  query(minX: number, minY: number, maxX: number, maxY: number): readonly RuntimeScene[] {
    if (![minX, minY, maxX, maxY].every(Number.isFinite) || minX > maxX || minY > maxY) return [];
    const visible = { minX, minY, maxX, maxY }, result: RuntimeScene[] = [];
    for (let x = Math.floor(minX / CELL); x <= Math.floor(maxX / CELL); x++) {
      for (let y = Math.floor(minY / CELL); y <= Math.floor(maxY / CELL); y++) {
        const key = `${x},${y}`;
        let scenes = this.cells.get(key);
        if (!scenes) {
          scenes = this.createCell(x, y);
          this.cells.set(key, scenes);
          if (this.cells.size > 512) this.cells.delete(this.cells.keys().next().value!);
        }
        for (const scene of scenes) if (scene.clearings.some(rect => overlaps(rect, visible))) result.push(scene);
      }
    }
    return result;
  }

  private createCell(cellX: number, cellY: number): readonly RuntimeScene[] {
    if (tileHash(cellX, cellY, 78001) >= 0.45) return [];
    const center = { x: (cellX + 0.5) * CELL + (tileHash(cellX, cellY, 78002) - 0.5) * 20,
      y: (cellY + 0.5) * CELL + (tileHash(cellX, cellY, 78003) - 0.5) * 20 };
    const biome = biomeAt(this.context, center);
    const choices = active.filter(scene => (scene.allowedBiomes ?? scene.preferredBiomes).includes(biome));
    const entry = choices[Math.floor(tileHash(cellX, cellY, 78004) * choices.length)];
    if (!entry) return [];
    const fragment = fragments[`/public/map-fragments/${entry.id}.json`];
    if (!fragment) return [];
    const sourceObjects = fragment.towns.flatMap(town => town.objects);
    if (!sourceObjects.length) return [];
    const source = [...(fragment.clearings ?? [])];
    // Older fragments have no clearings; fill only small contact pockets, never their town radius.
    for (const object of sourceObjects) if (!source.some(rect => contains(rect, object.worldX, object.worldY))) {
      source.push({ minX: object.worldX - 1.1, minY: object.worldY - 1.1, maxX: object.worldX + 1.1, maxY: object.worldY + 1.1 });
    }
    const sourcePoints = source.flatMap(corners), sourceBounds = bounds(sourcePoints);
    const transform: Transform = { x: center.x - (sourceBounds.minX + sourceBounds.maxX) / 2,
      y: center.y - (sourceBounds.minY + sourceBounds.maxY) / 2, cos: 1, sin: 0, source };
    const tangent = roadTangents[entry.id];
    if (tangent) {
      let nearest: Segment | undefined, nearestDistance = 24;
      for (const segment of this.segments) if (segment.main) {
        const d = distance(center, segment);
        if (d < nearestDistance) { nearest = segment; nearestDistance = d; }
      }
      if (!nearest) return [];
      const anchor = projection(center, nearest);
      const angle = Math.atan2(nearest.b.y - nearest.a.y, nearest.b.x - nearest.a.x) - Math.atan2(tangent[1], tangent[0]);
      transform.cos = Math.cos(angle); transform.sin = Math.sin(angle);
      const length = Math.hypot(nearest.b.x - nearest.a.x, nearest.b.y - nearest.a.y);
      const tx = (nearest.b.x - nearest.a.x) / length, ty = (nearest.b.y - nearest.a.y) / length;
      const nx = -ty, ny = tx;
      const local = sourcePoints.map(point => ({ x: point.x * transform.cos - point.y * transform.sin, y: point.x * transform.sin + point.y * transform.cos }));
      const minNormal = Math.min(...local.map(p => p.x * nx + p.y * ny));
      const minTangent = Math.min(...local.map(p => p.x * tx + p.y * ty));
      const maxTangent = Math.max(...local.map(p => p.x * tx + p.y * ty));
      transform.x = anchor.x + nx * (ROAD_CLEARANCE - minNormal) - tx * (minTangent + maxTangent) / 2;
      transform.y = anchor.y + ny * (ROAD_CLEARANCE - minNormal) - ty * (minTangent + maxTangent) / 2;
    }
    const worldPoints = sourcePoints.map(p => forward(transform, p.x, p.y));
    const occupied = bounds(worldPoints);
    if (occupied.minX < cellX * CELL + GAP || occupied.maxX > (cellX + 1) * CELL - GAP
      || occupied.minY < cellY * CELL + GAP || occupied.maxY > (cellY + 1) * CELL - GAP) return [];
    for (const town of this.context.towns) {
      const closest = { x: Math.max(occupied.minX, Math.min(town.cx, occupied.maxX)), y: Math.max(occupied.minY, Math.min(town.cy, occupied.maxY)) };
      if (Math.hypot(closest.x - town.cx, closest.y - town.cy) < town.radius + 5
        || (town.authoredBounds && overlaps(occupied, town.authoredBounds))) return [];
    }
    const objects = sourceObjects.map(object => {
      const point = forward(transform, object.worldX, object.worldY);
      return { ...object, id: `${cellX},${cellY}:${entry.id}:${object.id}`, worldX: point.x, worldY: point.y };
    });
    if ([...worldPoints, ...objects.map(o => ({ x: o.worldX, y: o.worldY }))].some(p => biomeAt(this.context, p) !== biome)) return [];
    // Check full clearing rectangles against every existing road, including bends and trail junctions.
    // Work runs only at macrocell creation; it does not introduce another road spatial index.
    for (const segment of this.clearanceSegments) {
      const a = inverse(transform, segment.a.x, segment.a.y), b = inverse(transform, segment.b.x, segment.b.y);
      for (const rect of source) {
        if (distanceToRect({ a, b, main: segment.main }, rect) < ROAD_CLEARANCE - 1e-7) return [];
      }
    }
    const scene: RuntimeScene = { id: `${cellX},${cellY}:${entry.id}`, sceneKey: entry.id, biome, objects,
      clearings: source.map(rect => bounds(corners(rect).map(p => forward(transform, p.x, p.y)))) };
    transforms.set(scene, transform);
    return [scene];
  }
}
